import { LOCALES } from "common/constants";
import { getSchemaMarkup } from "common/FunctionUtils";
import { blobURL } from "common/StringUtils";
import Footer from "components/layout/Footer";
import Header from "components/layout/Header";
import React, { useEffect } from "react";
import { Button, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

export const FreelanceFreelancing = ({ dashboardFooter }) => {
  const schemaMarkup = getSchemaMarkup("frilans-frilansarbete");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  let language = localStorage.getItem("i18nextLng");
  const freelanceFreelancingBanner = `${blobURL}/freelanceFreelancing.png`;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  return (
    <div>
      <Helmet>
        {schemaMarkup && (
          <script type="application/ld+json">
            {JSON.stringify(schemaMarkup)}
          </script>
        )}
        <title>
          {t(
            "A Complete Guide to Freelancing and Freelance Work - Tips for Success | Invozio"
          )}
        </title>
        <meta
          name="description"
          content={t(
            "Discover our complete guide to freelancing and freelance work! Learn how to get started, manage clients, and build a successful freelance career with flexibility and freedom."
          )}
        />
        {language === LOCALES.EN_US ? (
          <link
            rel="canonical"
            href="https://invozio.com/freelance-freelancing"
          />
        ) : (
          <link
            rel="canonical"
            href="https://invozio.com/frilans-frilansarbete"
          />
        )}
      </Helmet>

      <Header />
      <Container className="p-0 self_new negotiate neg_rspn">
        <div className="guidance">
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center title">
                <div className="col-md-6">
                  <div className="page-heading">
                    <h2 className="purple">
                      {t("Complete Guide to Freelancing")}
                    </h2>
                    {!user && (
                      <Button
                        className="mt-2 create-account filled home_create_account"
                        onClick={() => {
                          navigate(
                            language === LOCALES.EN_US
                              ? `/emailregister?lang=${LOCALES.EN_US}`
                              : `/e-postregister?lang=${LOCALES.SV_SE}`
                          );
                          sessionStorage.setItem("accountType", 1);
                        }}
                      >
                        {t("Create Free Account")}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="image-group guidance text-right">
                    <img
                      height={300}
                      src={freelanceFreelancingBanner}
                      alt={t("freelance, freelance work")}
                      loading="lazy"
                      style={{
                        textAlign: "right",
                        maxWidth: "100%",
                        height: "auto",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h1 className="purple">
                    {t(
                      "Guide to Freelancing and Freelance Work: How to Get Started and Succeed"
                    )}
                  </h1>
                  <p>
                    {t(
                      "Freelancing has become an increasingly popular way of working in recent years, especially with the digital revolution that allows people to work independently of location. For many, it is an opportunity to create a balance between work and personal life, gain more flexibility, and choose their own projects. But what does freelancing actually mean, and how can you succeed as a freelancer? In this guide, we will cover what freelancing entails, how to get started, and provide you with concrete tips for succeeding as a freelancer."
                    )}
                  </p>
                  <h2 className="purple">{t("What is freelance work?")}</h2>
                  <p>
                    {t(
                      "Freelance work means working for various clients without being traditionally employed by them. As a freelancer, you are an independent professional who offers your services to businesses or individuals on a contract basis, often for a set period or for a specific project. Many professions are suitable for freelance work, such as:"
                    )}
                  </p>
                  <ul>
                    <li className="self_emp_list">
                      {t(
                        "Writing (copywriting, journalism, technical writing)"
                      )}
                    </li>
                    <li className="self_emp_list">
                      {t("Web and app development")}
                    </li>
                    <li
                      className="
                    self_emp_list"
                    >
                      {t("Photography and video production")}
                    </li>
                    <li className="self_emp_list">
                      {t("Marketing and social media")}
                    </li>
                    <li className="self_emp_list">
                      {t("Translation and language editing")}
                    </li>
                    <li className="self_emp_list">
                      {t("Consultants specialized in all areas")}
                    </li>
                  </ul>

                  <Link
                    className="mb-2"
                    to={`${
                      language === LOCALES.EN_US
                        ? `/freelance-consultant-salary-invoice-without-a-company?lang=${LOCALES.EN_US}`
                        : `/frilans-konsult-lon-fakturera-utan-foretag?lang=${LOCALES.SV_SE}`
                    }`}
                    style={{ textDecoration: "underline", fontSize: 16 }}
                  >
                    {t("Learn more about consultants and invoicing.")}
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section mt-2">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Benefits of being a freelancer")}
                  </h2>
                  <p>
                    {t(
                      "There are many benefits to working as a freelancer, which is one of the reasons it has become such a popular career choice. Here are some of the main advantages:"
                    )}
                  </p>
                  <ol>
                    <li>
                      <strong>{t("Flexibility")}:</strong>{" "}
                      {t(
                        "You can usually control your own time, which means you can work when and from wherever you want, as long as you meet your deadlines."
                      )}
                    </li>
                    <li>
                      <strong>{t("Freedom to choose assignments")}:</strong>{" "}
                      {t(
                        "You have the opportunity to choose the projects and clients you want to work with. This gives you a strong sense of control and autonomy."
                      )}
                    </li>
                    <li>
                      <strong>{t("Potential for higher income")}:</strong>{" "}
                      {t(
                        "Since you charge per assignment or hour and are not limited by a fixed salary, your income can be higher than in a traditional employment, especially if you develop a specialized skill set."
                      )}
                    </li>
                    <li>
                      <strong>
                        {t("Opportunity to work on various projects")}:
                      </strong>{" "}
                      {t(
                        "As a freelancer, you can work on a diverse range of projects, which can be both stimulating and developmental."
                      )}
                    </li>
                  </ol>
                </div>

                <div className="guide">
                  <h2 className="purple">{t("Challenges of Freelancing")}</h2>
                  <p>
                    {t(
                      "Despite the many advantages of freelancing, there are also challenges to consider:"
                    )}
                  </p>

                  <ol>
                    <li>
                      <strong>{t("Irregular income")}:</strong>{" "}
                      {t(
                        "As a freelancer, your income can vary from month to month, and it may take time to build a steady client base."
                      )}
                    </li>
                    <li>
                      <strong>{t("Lack of security")}:</strong>{" "}
                      {t(
                        "You do not have the same job security or benefits such as sick pay, vacation compensation, and pension savings that a salaried employee may have."
                      )}
                    </li>
                    <li>
                      <strong>
                        {t("Own responsibility for marketing and sales")}:
                      </strong>{" "}
                      {t(
                        "It's not enough to just be good at what you do; you also need to market yourself and sell your services to attract new clients."
                      )}
                    </li>
                    <li>
                      <strong>{t("Loneliness")}:</strong>{" "}
                      {t(
                        "Many freelancers experience a sense of loneliness as they miss the social interaction with colleagues that often exists in traditional work environments."
                      )}
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="purple row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("How to Get Started as a Freelancer?")}
                  </h2>
                  <p>
                    {t(
                      "Starting as a freelancer requires careful planning and determination. Here are some basic steps to get started:"
                    )}
                  </p>
                  <h3 className="purple">
                    {t("Decide what services you want to offer")}
                  </h3>
                  <p>
                    {t(
                      "The first step is to clarify what type of services you want to offer. Consider what you are good at and what the market demands. Maybe you excel at writing or designing? Or perhaps you have skills in programming? It's important to choose an area where you have both expertise and interest."
                    )}
                  </p>
                  <h3 className="purple">{t("Build a portfolio")}</h3>
                  <p>
                    {t(
                      "A portfolio is an essential part of your marketing as a freelancer. It showcases what you can do and gives potential clients an idea of the quality of your work. If you don't have any previous projects to display, you can create sample projects to demonstrate your skills."
                    )}
                  </p>
                  <h3 className="purple">{t("Determine your pricing")}</h3>
                  <p>
                    {t(
                      "Pricing can be one of the most challenging aspects when you start as a freelancer. Here, we have gathered a"
                    )}
                    <Link
                      className="ml-1 mr-1"
                      to={`${
                        language === LOCALES.EN_US
                          ? `/how-much-should-I-invoice-as-a-freelancer?lang=${LOCALES.EN_US}`
                          : `/hur-mycket-ska-jag-fakturera-som-frilansare?lang=${LOCALES.SV_SE}`
                      }`}
                      style={{ textDecoration: "underline" }}
                    >
                      {t("guide for invoicing")}
                    </Link>
                    {t(
                      "for different occupational categories. You need to set a price that reflects your skills, experience, and the time it takes to complete the assignment. One tip is to start by researching what other freelancers in your field charge and adjust accordingly."
                    )}
                  </p>
                  <h3 className="purple">
                    {t("Get the Right Tools and Resources")}
                  </h3>
                  <p>
                    {t(
                      "Depending on your profession, having access to the right tools can be crucial. For a graphic designer, this might mean software like Adobe Creative Suite, while a freelance writer may need tools to manage grammar and style."
                    )}
                  </p>
                  <h3 className="purple">{t("Network and Find Clients")}</h3>
                  <p>
                    {t(
                      "To get assignments, you need to find clients. You can do this in several ways:"
                    )}
                  </p>
                  <ul>
                    <li className="self_emp_list">
                      {t(
                        "Join freelance platforms like Upwork, Fiverr, or Freelancer, where companies are looking for freelancers for short- and long-term projects."
                      )}
                    </li>

                    <li className="self_emp_list">
                      {t(
                        "Use social media platforms like LinkedIn or Instagram to market your services and reach potential clients."
                      )}
                    </li>
                    <li className="self_emp_list">
                      {t(
                        "Build relationships with people in your industry by attending conferences, courses, and other networking events."
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h3 className="purple">
                    {t("Manage your time and finances")}
                  </h3>
                  <p>
                    {t(
                      "Freelancers need to be skilled at managing their time efficiently. It's easy to lose track of projects and deadlines, so it's a good idea to use project management and time planning tools such as"
                    )}
                    <a
                      className="ml-1 mr-1"
                      target="_blank"
                      href="https://trello.com"
                      style={{ textDecoration: "underline" }}
                      rel="noopener noreferrer"
                    >
                      {t("Trello")},
                    </a>
                    <a
                      className="mr-1"
                      target="_blank"
                      href="https://asana.com/"
                      style={{ textDecoration: "underline" }}
                      rel="noopener noreferrer"
                    >
                      {t("Asana")}
                    </a>

                    {t(
                      "or Toggl. You also need to manage invoicing, and instead of starting your own company, Invozio handles the entire invoicing process for you."
                    )}
                  </p>
                  <Link
                    className="mr-1"
                    to={`${
                      language === LOCALES.EN_US
                        ? `/freelance-consultant-salary-invoice-without-a-company?lang=${LOCALES.EN_US}`
                        : `/frilans-konsult-lon-fakturera-utan-foretag?lang=${LOCALES.SV_SE}`
                    }`}
                    style={{ textDecoration: "underline" }}
                  >
                    {t(
                      "Read more here about what suits you best: running a business or invoicing without a company"
                    )}
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="purple row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Tips for Succeeding as a Freelancer")}
                  </h2>
                  <p>
                    {t(
                      "Once you've started as a freelancer, a lot depends on staying organized and continually evolving. Here are some tips for long-term success:"
                    )}
                  </p>

                  <h3 className="purple">{t("Be professional")}</h3>
                  <p>
                    {t(
                      "Even though you work independently, it’s important to maintain a high professional standard. Always deliver on time, keep communication with clients clear, and be meticulous with invoicing."
                    )}
                  </p>
                  <h3 className="purple">{t("Develop Your Skills")}</h3>
                  <p>
                    {t(
                      "The world is changing rapidly, and as a freelancer, it’s essential to stay updated. Participate in courses and training to enhance your skills and broaden your expertise, which can, in turn, lead to more and more lucrative assignments."
                    )}
                  </p>

                  <h3 className="purple">
                    {t("Build long-lasting customer relationships")}
                  </h3>
                  <p>
                    {t(
                      "Building good relationships with your clients can lead to repeat assignments and referrals to other potential clients. Always make sure to provide excellent service and exceed the client's expectations."
                    )}
                  </p>

                  <h3 className="purple">{t("Be open to feedback")}</h3>
                  <p>
                    {t(
                      "Feedback from clients is invaluable. It helps you understand what you’re doing well and what you can improve. Don’t be afraid to ask for feedback after completing projects, and use it to grow as a freelancer."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Let Invozio help you as a freelancer")}
                  </h2>
                  <p>
                    {t(
                      "Freelancing can be a very rewarding career path that offers freedom, flexibility, and the potential for higher income. However, it also requires a lot of planning, discipline, and hard work. By following the steps above and maintaining a professional approach, you can build a successful career as a freelancer."
                    )}
                  </p>
                  <p>
                    {t(
                      "Remember that networking, marketing, and constantly developing your skills are key to long-term success."
                    )}
                  </p>
                  <div className="text-center mt-5 mb-4">
                    <Button
                      className="self_emp m-auto create-account filled home_create_account"
                      onClick={() => {
                        navigate(
                          language === LOCALES.EN_US
                            ? `/emailregister?lang=${LOCALES.EN_US}`
                            : `/e-postregister?lang=${LOCALES.SV_SE}`
                        );
                        sessionStorage.setItem("accountType", 1);
                      }}
                    >
                      {t("Start invoicing today")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Container>
      <Footer dashboardFooter={dashboardFooter} />
    </div>
  );
};
